import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiSearch, FiEye, FiEdit, FiTrash2, FiRefreshCw, FiUpload, FiDownload, FiMessageSquare, FiCopy } from 'react-icons/fi';
import { prospectsAPI, settingsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { VALID_COUNTRIES, getCountryConfig } from '../../config/crmConfig';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const ProspectList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canPerformAction, user: currentUser } = useAuth();
  const { activeCountry } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [bulkOwnerId, setBulkOwnerId] = useState('');
  const [bulkTargetCountry, setBulkTargetCountry] = useState('');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignStep, setAssignStep] = useState(1);
  const [transferTeam, setTransferTeam] = useState([]);
  const [transferTeamLoading, setTransferTeamLoading] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importUniqueOnly, setImportUniqueOnly] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityProspect, setActivityProspect] = useState(null);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'Call',
    activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
    activity_time: '',
    next_followup: '',
    notes: ''
  });

  const typeFilter = searchParams.get('type') || '';
  const statusFilter = searchParams.get('status') || '';
  const sourceFilter = searchParams.get('source') || '';
  const search = searchParams.get('search') || '';
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || 10;

  const setFilter = useCallback((key, val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val) next.set(key, val); else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setFilter('search', searchInput);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  const canBulkAssign = ['super_admin', 'admin'].includes(String(currentUser?.role || '').toLowerCase());
  const canImportCsv = ['super_admin', 'admin'].includes(String(currentUser?.role || '').toLowerCase());
  const copyToClipboard = useCallback(async (text, kind) => {
    const value = String(text || '').trim();
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(kind === 'phone' ? 'Phone number copied' : 'Email copied');
    } catch (_) {
      toast.error('Copy failed');
    }
  }, []);

  const prospectColumns = useMemo(() => [
    ...(canBulkAssign ? [{ key: 'checkbox', label: '', locked: true }] : []),
    { key: 'prospect_ref', label: t('prospects.ref') },
    { key: 'name', label: t('prospects.name') },
    { key: 'prospect_type', label: t('prospects.type') },
    { key: 'contact_number', label: t('prospects.contact') },
    { key: 'email', label: t('prospects.email') },
    { key: 'status', label: t('prospects.statusLabel') },
    { key: 'owner_name', label: t('prospects.owner') },
    { key: 'created_at', label: t('prospects.created') },
    { key: 'log_activity', label: t('prospects.logActivity') },
    { key: 'source', label: t('common.source', 'Source'), defaultVisible: false },
    { key: 'decision_maker', label: t('prospects.decisionMaker', 'Decision Maker'), defaultVisible: false },
    { key: 'country', label: t('common.country'), defaultVisible: false },
    { key: 'state', label: t('common.state', 'State'), defaultVisible: false },
    { key: 'city', label: t('common.city'), defaultVisible: false },
    { key: 'passport_no', label: t('prospects.passport', 'Passport No'), defaultVisible: false },
    { key: 'gender', label: t('common.gender', 'Gender'), defaultVisible: false },
    { key: 'language', label: t('prospects.language', 'Language'), defaultVisible: false },
    { key: 'current_degree', label: t('prospects.currentDegree', 'Current Degree'), defaultVisible: false },
    { key: 'interested_course', label: t('prospects.interestedCourse', 'Interested Course'), defaultVisible: false },
    { key: 'remarks', label: t('common.remarks', 'Remarks'), defaultVisible: false },
    { key: 'created_by_name', label: t('prospects.createdBy', 'Created By'), defaultVisible: false },
    { key: 'actions', label: t('common.actions'), locked: true },
  ], [canBulkAssign, t]);

  const { isVisible, toggleColumn, resetToDefault, columns: prosColDefs } = useColumnPreferences('prospects', prospectColumns);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });

  useEffect(() => {
    settingsAPI.getProspectStatuses().catch(() => ({ data: [] })).then((statusesRes) => {
      setStatuses(statusesRes.data || []);
    });
  }, []);

  const filteredStatuses = useMemo(() => {
    if (!typeFilter) return statuses.filter(s => s.status === 'active');
    return statuses.filter(s => s.status === 'active' && s.prospect_group === typeFilter);
  }, [statuses, typeFilter]);

  useEffect(() => {
    setSelectedProspects([]);
  }, [typeFilter, statusFilter, sourceFilter, search, page]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('prospects.deleteConfirm'))) return;
    try {
      await prospectsAPI.delete(id);
      toast.success(t('prospects.deleted'));
      loadData();
    } catch {
      toast.error(t('prospects.deleteFailed'));
    }
  };

  const getStatusOptionsForType = useCallback((prospectType, currentStatus) => {
    const active = (statuses || [])
      .filter((s) => s.status === 'active' && s.prospect_group === prospectType)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map((s) => s.status_name);
    if (currentStatus && !active.includes(currentStatus)) active.unshift(currentStatus);
    return active;
  }, [statuses]);

  const handleStatusUpdate = async (prospectId, nextStatus, currentStatus) => {
    if (!nextStatus || nextStatus === currentStatus) return;
    try {
      setUpdatingStatusId(prospectId);
      await prospectsAPI.update(prospectId, { status: nextStatus });
      setProspects((prev) => prev.map((p) => (
        p.prospect_id === prospectId ? { ...p, status: nextStatus } : p
      )));
      toast.success(t('prospects.statusUpdated'));
    } catch {
      toast.error(t('prospects.statusUpdateFailed'));
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const statusBadgeColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'new') return { bg: '#EEF2FF', color: '#4F46E5' };
    if (s === 'contacted') return { bg: '#FEF3C7', color: '#92400E' };
    if (s === 'interested') return { bg: '#ECFDF5', color: '#059669' };
    if (s === 'not interested') return { bg: '#FEF2F2', color: '#DC2626' };
    if (s === 'converted') return { bg: '#D1FAE5', color: '#065F46' };
    return { bg: '#F1F5F9', color: '#475569' };
  };

  const statusSelectStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'converted') {
      return {
        background: '#D1FAE5',
        color: '#065F46',
        borderColor: '#86EFAC'
      };
    }
    return {
      background: 'var(--card-bg)',
      color: 'var(--text)',
      borderColor: 'var(--border-color)'
    };
  };

  const columnFilterDefs = useMemo(() => {
    const statusOpts = Array.from(new Set((prospects || []).map((p) => String(p.status || '')).filter(Boolean)));
    const ownerOpts = Array.from(new Set((prospects || []).map((p) => String(p.owner_name || '')).filter(Boolean)));
    const typeOpts = ['B2B', 'B2C'];
    return [
      { key: 'prospect_ref', label: t('prospects.ref'), field: 'prospect_ref', type: 'text' },
      { key: 'name', label: t('prospects.name'), field: 'name', type: 'text' },
      { key: 'prospect_type', label: t('prospects.type'), field: 'prospect_type', type: 'multi', options: typeOpts.map((v) => ({ value: v, label: v })) },
      { key: 'contact_number', label: t('prospects.contact'), field: 'contact_number', type: 'text' },
      { key: 'email', label: t('prospects.email'), field: 'email', type: 'text' },
      { key: 'status', label: t('prospects.statusLabel'), field: 'status', type: 'multi', options: statusOpts.map((v) => ({ value: v, label: v })) },
      { key: 'owner_name', label: t('prospects.owner'), field: 'owner_name', type: 'multi', options: ownerOpts.map((v) => ({ value: v, label: v })) },
      { key: 'created_at', label: t('prospects.created'), field: 'created_at', type: 'date' },
      { key: 'source', label: t('common.source', 'Source'), field: 'source', type: 'text' },
      { key: 'decision_maker', label: t('prospects.decisionMaker', 'Decision Maker'), field: 'decision_maker', type: 'text' },
      { key: 'country', label: t('common.country'), field: 'country', type: 'text' },
      { key: 'state', label: t('common.state', 'State'), field: 'state', type: 'text' },
      { key: 'city', label: t('common.city'), field: 'city', type: 'text' },
      { key: 'passport_no', label: t('prospects.passport', 'Passport No'), field: 'passport_no', type: 'text' },
      { key: 'remarks', label: t('common.remarks', 'Remarks'), field: 'remarks', type: 'text' }
    ];
  }, [prospects, t]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const filteredProspects = useMemo(
    () => runTableFilters(prospects, tableFilters, tableFilters, [], columnFilterDefs),
    [prospects, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );
  const pagedFilteredProspects = useMemo(() => {
    if (!clientTableFilterMode) return filteredProspects;
    const start = (page - 1) * limit;
    return filteredProspects.slice(start, start + limit);
  }, [clientTableFilterMode, filteredProspects, page, limit]);
  const listTotalForPaging = clientTableFilterMode ? filteredProspects.length : total;
  const totalPages = Math.max(1, Math.ceil(listTotalForPaging / limit)) || 1;
  const startItem = listTotalForPaging === 0 ? 0 : ((page - 1) * limit) + 1;
  const endItem = Math.min(page * limit, listTotalForPaging);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(listTotalForPaging / limit));
    if (page > tp) setFilter('page', String(tp));
  }, [clientTableFilterMode, filteredProspects.length, total, limit, page, setFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const params = {
        page: active ? 1 : page,
        limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : limit
      };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (search) params.search = search;
      const res = await prospectsAPI.getAll(params);
      setProspects(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error(t('prospects.loadFailed'));
    }
    setLoading(false);
  }, [page, limit, typeFilter, statusFilter, sourceFilter, search, t, clientTableFilterMode]);

  useEffect(() => { loadData(); }, [loadData]);

  const allSelectedOnPage = prospects.length > 0 && selectedProspects.length === prospects.length;

  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedProspects([]);
    } else {
      setSelectedProspects(prospects.map(p => p.prospect_id));
    }
  };

  const toggleSelectOne = (prospectId) => {
    setSelectedProspects((prev) => (
      prev.includes(prospectId)
        ? prev.filter(id => id !== prospectId)
        : [...prev, prospectId]
    ));
  };

  const resetAssignWizard = () => {
    setAssignModalOpen(false);
    setAssignStep(1);
    setBulkOwnerId('');
    setBulkTargetCountry('');
    setTransferTeam([]);
    setTransferTeamLoading(false);
  };

  const handleOpenAssignWizard = () => {
    if (selectedProspects.length === 0) {
      toast.error(t('prospects.selectProspectsFirst', { defaultValue: 'Please select one or more prospects' }));
      return;
    }
    setBulkTargetCountry(String(activeCountry || 'INDIA').toUpperCase());
    setBulkOwnerId('');
    setTransferTeam([]);
    setAssignStep(1);
    setAssignModalOpen(true);
  };

  const handleAssignStep1Next = async () => {
    const tc = String(bulkTargetCountry || '').toUpperCase();
    if (!VALID_COUNTRIES.includes(tc)) {
      toast.error(t('prospects.pickCrmCountry', { defaultValue: 'Choose which CRM country these leads belong to' }));
      return;
    }
    setTransferTeamLoading(true);
    try {
      const res = await usersAPI.getSalesTeam({ crm_country: tc });
      setTransferTeam(res.data || []);
      setBulkOwnerId('');
      setAssignStep(2);
    } catch {
      toast.error(t('prospects.loadTeamFailed', { defaultValue: 'Could not load users for that country' }));
    } finally {
      setTransferTeamLoading(false);
    }
  };

  const handleAssignStep2Next = () => {
    if (!bulkOwnerId) {
      toast.error(t('prospects.pickOwner', { defaultValue: 'Please select a new owner' }));
      return;
    }
    setAssignStep(3);
  };

  const confirmBulkAssign = async () => {
    setBulkAssigning(true);
    const tc = String(bulkTargetCountry || '').toUpperCase();
    try {
      const res = await prospectsAPI.bulkAssign({
        prospect_ids: selectedProspects,
        owner_id: Number(bulkOwnerId),
        crm_country_code: tc
      });
      toast.success(t('prospects.assignSuccess', { defaultValue: 'Prospects updated (owner and CRM country where applicable)' }));
      const warnings = Array.isArray(res?.data?.warnings) ? res.data.warnings.filter(Boolean) : [];
      if (warnings.length) {
        toast.warn(warnings.join('\n'));
      }
      setSelectedProspects([]);
      resetAssignWizard();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || t('prospects.assignFailed', { defaultValue: 'Failed to assign selected prospects' }));
    }
    setBulkAssigning(false);
  };

  const downloadSampleCsv = (type) => {
    const b2bHeader = [
      'prospect_type', 'name', 'owner_email', 'contact_number', 'phone_country_code', 'email', 'source',
      'decision_maker', 'country', 'state', 'city', 'location_country_code', 'location_state_code', 'remarks'
    ];
    const b2bRow = [
      'B2B', 'ABC Agency', 'sales.user@company.com', '9876543210', '+91', 'agency@example.com', 'Cold Call',
      'John Manager', 'India', 'Gujarat', 'Ahmedabad', 'IN', 'GJ', 'Important agency'
    ];

    const b2cHeader = [
      'prospect_type', 'name', 'owner_email', 'first_name', 'last_name', 'title', 'contact_number', 'phone_country_code', 'email',
      'dob', 'gender', 'language', 'passport_no', 'current_degree', 'address', 'country', 'state', 'city',
      'location_country_code', 'location_state_code', 'zip_code', 'source', 'remarks'
    ];
    const b2cRow = [
      'B2C', 'Alice Martin', 'counsellor@company.com', 'Alice', 'Martin', 'Ms.', '9123456789', '+33', 'alice@example.com',
      '2001-01-25', 'Female', 'FRENCH', 'AA1234567', 'Bachelor', '12 Rue de Paris', 'France', 'Ile-de-France', 'Paris',
      'FR', 'IDF', '75001', 'Website', 'Potential student'
    ];

    const rows = type === 'B2B'
      ? [b2bHeader.join(','), b2bRow.map(v => `"${v}"`).join(',')]
      : [b2cHeader.join(','), b2cRow.map(v => `"${v}"`).join(',')];
    const csv = rows.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = type === 'B2B' ? 'prospects_b2b_sample.csv' : 'prospects_b2c_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCsv = async () => {
    if (!importFile) {
      toast.error('Please choose a CSV file');
      return;
    }
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('unique_contact_only', importUniqueOnly ? '1' : '0');
    setImportSubmitting(true);
    try {
      const res = await prospectsAPI.importCsv(formData);
      const imported = Number(res.data?.imported_count || 0);
      const removed = Number(res.data?.removed_count || 0);
      const errCount = Number(res.data?.error_count || 0);
      setImportResult({
        total: Number(res.data?.total_rows || 0),
        uploaded: imported,
        removed,
        failed: errCount,
        failedReasons: Array.isArray(res.data?.errors) ? res.data.errors : [],
        removedReasons: Array.isArray(res.data?.removed) ? res.data.removed : []
      });
      if (imported > 0) {
        toast.success(`Imported ${imported} prospects successfully`);
      }
      if (removed > 0) {
        toast.info(`Skipped ${removed} row(s) (duplicates/missing contact based on option).`);
      }
      if (errCount > 0) {
        const firstErr = Array.isArray(res.data?.errors) && res.data.errors.length ? ` First error: ${res.data.errors[0]}` : '';
        toast.warn(`${errCount} rows failed.${firstErr}`);
      }
      setImportFile(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to import CSV');
    }
    setImportSubmitting(false);
  };

  const openActivityModal = (prospect) => {
    setActivityProspect(prospect);
    setActivityForm({
      activity_type: 'Call',
      activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
      activity_time: '',
      next_followup: '',
      notes: ''
    });
    setActivityModalOpen(true);
  };

  const handleSaveActivity = async () => {
    if (!activityProspect?.prospect_id) return;
    if (!activityForm.activity_type || !activityForm.activity_date || !String(activityForm.notes || '').trim()) {
      toast.error(t('prospects.activityRequired'));
      return;
    }
    try {
      setActivitySaving(true);
      await prospectsAPI.createActivity(activityProspect.prospect_id, activityForm);
      toast.success(t('prospects.activityLogged'));
      setActivityModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || t('prospects.activityLogFailed'));
    } finally {
      setActivitySaving(false);
    }
  };

  return (
    <div>
      <Modal
        isOpen={assignModalOpen}
        onClose={() => {
          if (!bulkAssigning) resetAssignWizard();
        }}
        title={
          assignStep === 1
            ? t('prospects.assignStepCountry', { defaultValue: 'Transfer leads — CRM country (1/3)' })
            : assignStep === 2
              ? t('prospects.assignStepOwner', { defaultValue: 'Choose new owner (2/3)' })
              : t('prospects.assignStepConfirm', { defaultValue: 'Confirm transfer (3/3)' })
        }
        footer={(
          <>
            {assignStep === 1 && (
              <>
                <button type="button" className="btn btn-outline" onClick={resetAssignWizard} disabled={bulkAssigning || transferTeamLoading}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignStep1Next}
                  disabled={bulkAssigning || transferTeamLoading}
                >
                  {transferTeamLoading ? t('common.loading') : t('common.next')}
                </button>
              </>
            )}
            {assignStep === 2 && (
              <>
                <button type="button" className="btn btn-outline" onClick={() => setAssignStep(1)} disabled={bulkAssigning}>
                  {t('common.back', { defaultValue: 'Back' })}
                </button>
                <button type="button" className="btn btn-primary" onClick={handleAssignStep2Next} disabled={bulkAssigning}>
                  {t('common.next', { defaultValue: 'Next' })}
                </button>
              </>
            )}
            {assignStep === 3 && (
              <>
                <button type="button" className="btn btn-outline" onClick={() => setAssignStep(2)} disabled={bulkAssigning}>
                  {t('common.back', { defaultValue: 'Back' })}
                </button>
                <button type="button" className="btn btn-primary" onClick={confirmBulkAssign} disabled={bulkAssigning}>
                  {bulkAssigning ? t('common.saving') : t('prospects.confirmAssign', { defaultValue: 'Confirm & assign' })}
                </button>
              </>
            )}
          </>
        )}
      >
        {assignStep === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
              {t('prospects.assignCountryHelp', {
                defaultValue: 'Select the CRM country these leads should sit in after assignment. The lead record’s CRM country and owner will both be updated. Then you will pick an active user for that country.'
              })}
            </p>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>{t('prospects.crmCountryLabel', { defaultValue: 'CRM country' })}</label>
              <select
                value={bulkTargetCountry}
                onChange={(e) => setBulkTargetCountry(e.target.value)}
                style={{ width: '100%', maxWidth: 360, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)' }}
              >
                {VALID_COUNTRIES.map((code) => {
                  const cfg = getCountryConfig(code);
                  return (
                    <option key={code} value={code}>
                      {cfg.flag} {cfg.label}
                    </option>
                  );
                })}
              </select>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              {t('prospects.assignSelectedCount', { count: selectedProspects.length, defaultValue: '{{count}} prospect(s) selected' })}
            </p>
          </div>
        )}
        {assignStep === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
              {t('prospects.assignOwnerHelp', {
                country: getCountryConfig(bulkTargetCountry).label,
                defaultValue: 'Choose the user who will own these leads in {{country}}.'
              })}
            </p>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontWeight: 600 }}>{t('prospects.newOwner', { defaultValue: 'New owner' })}</label>
              <select
                value={bulkOwnerId}
                onChange={(e) => setBulkOwnerId(e.target.value)}
                style={{ width: '100%', maxWidth: 400, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)' }}
              >
                <option value="">{t('prospects.defaultOwner')}</option>
                {transferTeam.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name}{u.role ? ` (${u.role})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {transferTeam.length === 0 && !transferTeamLoading && (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--danger, #c0392b)' }}>
                {t('prospects.noUsersInCountry', { defaultValue: 'No active users found for this country. Add users or check access country.' })}
              </p>
            )}
          </div>
        )}
        {assignStep === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
            <p style={{ margin: 0 }}>
              {t('prospects.assignSummaryIntro', { defaultValue: 'You are about to update the following:' })}
            </p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <strong>{selectedProspects.length}</strong>{' '}
                {t('prospects.assignSummaryLeads', { defaultValue: 'prospect(s)' })}
              </li>
              <li>
                {t('prospects.assignSummaryCountry', {
                  country: `${getCountryConfig(bulkTargetCountry).flag} ${getCountryConfig(bulkTargetCountry).label}`,
                  defaultValue: 'CRM country: {{country}}'
                })}
              </li>
              <li>
                {t('prospects.assignSummaryOwner', {
                  name: transferTeam.find((u) => String(u.user_id) === String(bulkOwnerId))?.name || '—',
                  defaultValue: 'New owner: {{name}}'
                })}
              </li>
            </ul>
            {String(bulkTargetCountry).toUpperCase() !== String(activeCountry || '').toUpperCase() && (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                {t('prospects.crossCountryNote', {
                  defaultValue: 'Moving to another CRM country is only allowed for admin roles; activity history is moved with the lead.'
                })}
              </p>
            )}
          </div>
        )}
      </Modal>

      <div className="page-header">
        <div>
          <h1>{t('prospects.title')}</h1>
          <p className="page-subtitle">{t('prospects.subtitle', { count: clientTableFilterMode ? listTotalForPaging : total })}</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 8 }}>
          {canImportCsv && (
            <button className="btn btn-outline" onClick={() => setImportModalOpen(true)}>
              <FiUpload /> Import CSV
            </button>
          )}
          {canPerformAction('prospects', 'create') && (
            <button className="btn btn-primary" onClick={() => navigate('/prospects/new')}>
              <FiPlus /> {t('prospects.addBtn')}
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={importModalOpen}
        onClose={() => !importSubmitting && setImportModalOpen(false)}
        title="Import Prospects CSV"
        footer={(
          <>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setImportModalOpen(false);
                setImportResult(null);
                setImportUniqueOnly(false);
                setImportFile(null);
              }}
              disabled={importSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleImportCsv}
              disabled={importSubmitting}
            >
              {importSubmitting ? t('common.saving') : 'Import'}
            </button>
          </>
        )}
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => downloadSampleCsv('B2B')}>
              <FiDownload /> Download B2B Sample CSV
            </button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => downloadSampleCsv('B2C')}>
              <FiDownload /> Download B2C Sample CSV
            </button>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>CSV File</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setImportFile(e.target.files?.[0] || null);
                setImportResult(null);
              }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              Required columns: <code>prospect_type</code>, <code>name</code>. Owner can be set using <code>owner_email</code> (recommended), <code>owner_name</code>, or <code>owner_id</code>. For B2B provide <code>contact_number</code>. For B2C provide <code>email</code> or <code>contact_number</code>.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={importUniqueOnly}
              onChange={(e) => setImportUniqueOnly(e.target.checked)}
            />
            Upload only unique contact numbers (checks CSV + existing DB contacts)
          </label>
          {importResult && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--bg-secondary)' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Import Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))', gap: 8, fontSize: 13 }}>
                <div>Total data: <strong>{importResult.total}</strong></div>
                <div>Uploaded data: <strong>{importResult.uploaded}</strong></div>
                <div>Removed data: <strong>{importResult.removed}</strong></div>
                <div>Failed data: <strong>{importResult.failed}</strong></div>
              </div>
              {importResult.removedReasons.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Removed reasons</div>
                  <ul style={{ margin: '6px 0 0 16px', maxHeight: 110, overflow: 'auto', fontSize: 12 }}>
                    {importResult.removedReasons.slice(0, 30).map((msg, idx) => <li key={`rm-${idx}`}>{msg}</li>)}
                  </ul>
                </div>
              )}
              {importResult.failedReasons.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>Failed reasons</div>
                  <ul style={{ margin: '6px 0 0 16px', maxHeight: 140, overflow: 'auto', fontSize: 12 }}>
                    {importResult.failedReasons.slice(0, 30).map((msg, idx) => <li key={`er-${idx}`}>{msg}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={activityModalOpen}
        onClose={() => { if (!activitySaving) setActivityModalOpen(false); }}
        title={`${t('prospects.logActivity')}${activityProspect?.name ? ` - ${activityProspect.name}` : ''}`}
        footer={(
          <>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setActivityModalOpen(false)}
              disabled={activitySaving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveActivity}
              disabled={activitySaving}
            >
              {activitySaving ? t('common.saving') : t('prospects.saveActivity')}
            </button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('prospects.activityTypeLabel')} <span className="required">*</span></label>
            <select value={activityForm.activity_type} onChange={(e) => setActivityForm((f) => ({ ...f, activity_type: e.target.value }))}>
              {['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'].map((at) => <option key={at} value={at}>{at}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('prospects.activityDateLabel')} <span className="required">*</span></label>
            <input type="date" value={activityForm.activity_date} onChange={(e) => setActivityForm((f) => ({ ...f, activity_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('prospects.activityTimeLabel')}</label>
            <input type="time" value={activityForm.activity_time} onChange={(e) => setActivityForm((f) => ({ ...f, activity_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('prospects.nextFollowupDate')}</label>
            <input type="date" value={activityForm.next_followup} onChange={(e) => setActivityForm((f) => ({ ...f, next_followup: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('prospects.activityNotesLabel')} <span className="required">*</span></label>
            <textarea value={activityForm.notes} onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Modal>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '16px 20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['', 'B2B', 'B2C'].map(v => (
              <button key={v}
                className={`btn btn-sm ${typeFilter === v ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setFilter('type', v)}
                style={{ fontSize: 12, padding: '6px 14px' }}>
                {v || t('prospects.all')}
              </button>
            ))}
          </div>

          <select value={statusFilter} onChange={e => setFilter('status', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}>
            <option value="">{t('prospects.allStatuses')}</option>
            {filteredStatuses.map(s => (
              <option key={s.status_id} value={s.status_name}>{s.status_name}</option>
            ))}
          </select>

          <select value={sourceFilter} onChange={e => setFilter('source', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}>
            <option value="">{t('common.allSources', 'All Sources')}</option>
            <option value="Website Leads">Website Leads</option>
          </select>

          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-light)' }} />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={t('prospects.searchPlaceholder')}
              style={{ width: '100%', padding: '8px 8px 8px 34px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}
            />
          </div>

          <button className="btn btn-outline btn-sm" onClick={loadData} title={t('common.refresh')}>
            <FiRefreshCw size={14} />
          </button>
          <select
            value={String(limit)}
            onChange={(e) => setFilter('limit', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
          <ColumnCustomizer columns={prosColDefs} isVisible={isVisible} toggleColumn={toggleColumn} resetToDefault={resetToDefault} />
          {canBulkAssign && (
            <>
              <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {selectedProspects.length} {t('prospects.selectedLabel', { defaultValue: 'selected' })}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleOpenAssignWizard}
                disabled={bulkAssigning || selectedProspects.length === 0}
                style={{ whiteSpace: 'nowrap' }}
              >
                {t('prospects.assignTransferBtn', { defaultValue: 'Assign / transfer…' })}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" />
          </div>
        ) : prospects.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            <p style={{ fontSize: 16 }}>{t('prospects.noProspects')}</p>
            <p style={{ fontSize: 13 }}>{t('prospects.noProspectsHint')}</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    {canBulkAssign && (
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={allSelectedOnPage}
                          onChange={toggleSelectAll}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                    )}
                    {isVisible('prospect_ref') && <UnifiedFilterHeaderCell column={columnFilterMap.prospect_ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('name') && <UnifiedFilterHeaderCell column={columnFilterMap.name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('prospect_type') && <UnifiedFilterHeaderCell column={columnFilterMap.prospect_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('contact_number') && <UnifiedFilterHeaderCell column={columnFilterMap.contact_number} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('email') && <UnifiedFilterHeaderCell column={columnFilterMap.email} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('owner_name') && <UnifiedFilterHeaderCell column={columnFilterMap.owner_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('created_at') && <UnifiedFilterHeaderCell column={columnFilterMap.created_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('log_activity') && <th>{t('prospects.logActivity')}</th>}
                    {isVisible('source') && <UnifiedFilterHeaderCell column={columnFilterMap.source} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('decision_maker') && <UnifiedFilterHeaderCell column={columnFilterMap.decision_maker} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('country') && <UnifiedFilterHeaderCell column={columnFilterMap.country} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('state') && <UnifiedFilterHeaderCell column={columnFilterMap.state} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('city') && <UnifiedFilterHeaderCell column={columnFilterMap.city} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('passport_no') && <UnifiedFilterHeaderCell column={columnFilterMap.passport_no} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('gender') && <th>{t('common.gender', 'Gender')}</th>}
                    {isVisible('language') && <th>{t('prospects.language', 'Language')}</th>}
                    {isVisible('current_degree') && <th>{t('prospects.currentDegree', 'Current Degree')}</th>}
                    {isVisible('interested_course') && <th>{t('prospects.interestedCourse', 'Interested Course')}</th>}
                    {isVisible('remarks') && <UnifiedFilterHeaderCell column={columnFilterMap.remarks} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                    {isVisible('created_by_name') && <th>{t('prospects.createdBy', 'Created By')}</th>}
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilteredProspects.map(p => {
                    const badge = statusBadgeColor(p.status);
                    return (
                      <tr key={p.prospect_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/prospects/${p.prospect_id}`)}>
                        {canBulkAssign && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedProspects.includes(p.prospect_id)}
                              onChange={() => toggleSelectOne(p.prospect_id)}
                            />
                          </td>
                        )}
                        {isVisible('prospect_ref') && <td><strong style={{ color: 'var(--primary)' }}>{p.prospect_ref}</strong></td>}
                        {isVisible('name') && <td>{p.name}</td>}
                        {isVisible('prospect_type') && (
                        <td>
                          <span style={{
                            padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: p.prospect_type === 'B2B' ? '#EEF2FF' : '#FEF3C7',
                            color: p.prospect_type === 'B2B' ? '#4F46E5' : '#92400E'
                          }}>
                            {p.prospect_type}
                          </span>
                        </td>
                        )}
                        {isVisible('contact_number') && (
                          <td>
                            {p.contact_number ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(`${p.phone_country_code || ''} ${p.contact_number || ''}`.trim(), 'phone'); }}
                                style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                                title="Copy phone number"
                              >
                                <span>{`${p.phone_country_code || ''} ${p.contact_number || ''}`.trim()}</span>
                                <FiCopy size={13} />
                              </button>
                            ) : '-'}
                          </td>
                        )}
                        {isVisible('email') && (
                          <td>
                            {p.email ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(p.email, 'email'); }}
                                style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                                title="Copy email"
                              >
                                <span>{p.email}</span>
                                <FiCopy size={13} />
                              </button>
                            ) : '-'}
                          </td>
                        )}
                        {isVisible('status') && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {canPerformAction('prospects', 'update') ? (
                            <select
                              value={p.status || ''}
                              disabled={updatingStatusId === p.prospect_id}
                              onChange={(e) => handleStatusUpdate(p.prospect_id, e.target.value, p.status)}
                              style={{
                                minWidth: 150,
                                maxWidth: 210,
                                borderRadius: 8,
                                border: `1px solid ${statusSelectStyle(p.status).borderColor}`,
                                padding: '6px 8px',
                                background: statusSelectStyle(p.status).background,
                                color: statusSelectStyle(p.status).color,
                                fontSize: 12,
                                fontWeight: 600
                              }}
                            >
                              <option value="">{t('prospects.statusLabel')}</option>
                              {getStatusOptionsForType(p.prospect_type, p.status).map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.color }}>
                              {p.status}
                            </span>
                          )}
                        </td>
                        )}
                        {isVisible('owner_name') && <td>{p.owner_name || '-'}</td>}
                        {isVisible('created_at') && (
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                        </td>
                        )}
                        {isVisible('log_activity') && (
                        <td onClick={e => e.stopPropagation()}>
                          {canPerformAction('prospects', 'update') ? (
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => openActivityModal(p)}
                              title={t('prospects.logActivity')}
                            >
                              <FiMessageSquare size={14} /> {t('prospects.logActivity')}
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>-</span>
                          )}
                        </td>
                        )}
                        {isVisible('source') && <td style={{ fontSize: 13 }}>{p.source || '-'}</td>}
                        {isVisible('decision_maker') && <td style={{ fontSize: 13 }}>{p.decision_maker || '-'}</td>}
                        {isVisible('country') && <td style={{ fontSize: 13 }}>{p.country || '-'}</td>}
                        {isVisible('state') && <td style={{ fontSize: 13 }}>{p.state || '-'}</td>}
                        {isVisible('city') && <td style={{ fontSize: 13 }}>{p.city || '-'}</td>}
                        {isVisible('passport_no') && <td style={{ fontSize: 13 }}>{p.passport_no || '-'}</td>}
                        {isVisible('gender') && <td style={{ fontSize: 13 }}>{p.gender || '-'}</td>}
                        {isVisible('language') && <td style={{ fontSize: 13 }}>{p.language || '-'}</td>}
                        {isVisible('current_degree') && <td style={{ fontSize: 13 }}>{p.current_degree || '-'}</td>}
                        {isVisible('interested_course') && <td style={{ fontSize: 13 }}>{p.interested_course || '-'}</td>}
                        {isVisible('remarks') && <td style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.remarks || '-'}</td>}
                        {isVisible('created_by_name') && <td style={{ fontSize: 13 }}>{p.created_by_name || '-'}</td>}
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm btn-outline" onClick={() => navigate(`/prospects/${p.prospect_id}`)} title={t('common.view')}>
                              <FiEye size={14} />
                            </button>
                            {canPerformAction('prospects', 'update') && (
                              <button className="btn btn-sm btn-outline" onClick={() => navigate(`/prospects/${p.prospect_id}/edit`)} title={t('common.edit')}>
                                <FiEdit size={14} />
                              </button>
                            )}
                            {canPerformAction('prospects', 'delete') && (
                              <button className="btn btn-sm btn-outline" onClick={() => handleDelete(p.prospect_id)} title={t('common.delete')}
                                style={{ color: 'var(--danger)' }}>
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
                <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>
                  {t('common.prev')}
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                  .map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setFilter('page', String(p))}
                    >
                      {p}
                    </button>
                  ))}
                <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => setFilter('page', String(page + 1))}>
                  {t('common.next')}
                </button>
              </div>
            )}
            <div style={{ textAlign: 'center', padding: '0 0 12px', fontSize: 12, color: 'var(--text-muted)' }}>
              Showing {startItem}-{endItem} of {listTotalForPaging}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProspectList;
